import React, {useState, useMemo, useCallback, useRef, useEffect} from 'react';
import {Box, List, ListSubheader, Typography} from '@mui/material';
import './MyTranscripts.scss';
import {groupTranscriptByDate} from '../../../../../utils/groupTranscriptsByDate';
import {t} from 'i18next';
import {useTranscriptPanelSort} from '../../transcript-panel/TranscriptPanelContext';
import {
  isDuplicateTranscriptNameError,
  TranscriptItemDataType,
  TranscriptSortKey,
} from '../../AiInvestigateUtils';
import ArrButton from '../../../../common/arr-button/ArrButton';
import {ReactComponent as PlusIcon} from '../../../../../assets/images/add.svg';
import {
  useDeleteInvestigateTranscript,
  useEditInvestigateTranscript,
} from '../../../../../hooks/ai/investigate/useInvestigate';
import TranscriptListItem from '../transcript-list-item/TranscriptListItem';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../../../shared/constants';

interface Props {
  transcripts: TranscriptItemDataType[];
  setTranscripts: React.Dispatch<
    React.SetStateAction<TranscriptItemDataType[]>
  >;
  onCreateClick: () => void;
  hasIndexes: boolean;
  selectedTranscriptId?: number;
  onTranscriptSelect?: (selectedTranscript: {
    transcriptId: number;
    isOwner: boolean;
    indexId: number;
    indexName: string;
    isActiveIndex: boolean;
    transcriptName: string;
    ownerName: string;
    documentCount?: number;
  }) => void;
  isFetching: boolean;
  isLoading: boolean;
  onScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  onTranscriptDeleted?: (id: number) => void;
}
const MyTranscripts: React.FC<Props> = ({
  transcripts,
  setTranscripts,
  onCreateClick,
  hasIndexes,
  selectedTranscriptId,
  onTranscriptSelect,
  isFetching,
  isLoading,
  onScroll,
  onTranscriptDeleted,
}) => {
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const selectedSortKey = useTranscriptPanelSort();
  const [focusedId, setFocusedId] = useState(-1);
  const focusViaKeyboardRef = useRef(false);
  const isCreateDisabled = hasIndexes === false;
  let flatIndexCounter = 0;

  const isNewestFirst =
    selectedSortKey === TranscriptSortKey.ModifiedNewest ||
    selectedSortKey === TranscriptSortKey.CreatedNewest;
  const sortType = selectedSortKey.includes('modified')
    ? 'modified'
    : 'created';
  const groupData = useMemo(
    () => groupTranscriptByDate(transcripts, sortType, isNewestFirst),
    [transcripts, sortType, isNewestFirst]
  );
  const sortedGroups = Object.entries(groupData).sort((a, b) =>
    isNewestFirst
      ? b[1].sortDate - a[1].sortDate
      : a[1].sortDate - b[1].sortDate
  );

  const flatItems = useMemo(() => {
    return sortedGroups.flatMap(([dateLabel, groups]: any) =>
      groups.items.map((item: any) => ({
        ...item,
        dateLabel,
      }))
    );
  }, [sortedGroups]);

  const orderedIds = useMemo(
    () => flatItems.map((item) => item.transcriptId),
    [flatItems]
  );

  const {mutateAsync: editTranscript} = useEditInvestigateTranscript();

  const handleRenameTranscript = useCallback(
    async (id: number, newName: string, newDescription: string) => {
      try {
        const {transcriptId, transcriptName, description, updatedDate} =
          await editTranscript({
            id,
            payload: {
              transcriptName: newName,
              description: newDescription,
            },
          });

        setTranscripts((prev) =>
          prev.map((item) =>
            item.transcriptId === transcriptId
              ? {
                  ...item,
                  transcriptName,
                  description,
                  updatedDate,
                }
              : item
          )
        );
        return true;
      } catch (error) {
        setToastMessage(
          isDuplicateTranscriptNameError(error)
            ? t('ai.investigate.listItem.editModalContent.duplicateChatName')
            : t('ai.investigate.listItem.editModalContent.errorMessage')
        );
        setShowToast(true);
        return false;
      }
    },
    [editTranscript, setTranscripts]
  );
  const isLoadingRef = useRef(false);

  useEffect(() => {
    isLoadingRef.current = false;
  }, [transcripts]);

  const {mutate: deleteTranscript} = useDeleteInvestigateTranscript();

  const handleDeleteTranscript = useCallback(
    (id: number) => {
      deleteTranscript(id, {
        onSuccess: () => {
          setTranscripts((prev) =>
            prev.filter((item) => item.transcriptId !== id)
          );
          onTranscriptDeleted?.(id);
        },
        onError: (err) => {
          console.error('Delete failed', err);
        },
      });
    },
    [deleteTranscript, onTranscriptDeleted]
  );

  return (
    <Box
      className="transcript-list-container"
      onScroll={onScroll}
      data-testid="my-transcripts-container"
    >
      <List className="transcript-list" disablePadding>
        {isLoading && flatItems.length === 0 && (
          <Box className="spinner-center">
            <ArrLoadingSpinner size={32} />
          </Box>
        )}

        {!isLoading && flatItems.length === 0 && (
          <Box className="no-transcripts-container">
            <Typography
              className="transcript-panel-no-transcripts typo-text body3 size"
              data-testid="my-transcripts-no-transcripts"
            >
              {t('ai.investigate.transcriptPanel.noTranscripts')}
            </Typography>
            <ArrButton
              variant="primary"
              className="create-transcript-button"
              size="medium"
              onClick={onCreateClick}
              dataTestId="create-transcript-button"
              aria-label={t('ai.investigate.listItem.createBtn')}
              disabled={isCreateDisabled}
              endIcon={
                <PlusIcon
                  className={`plus-icon ${isCreateDisabled ? 'icon-inactive' : ''}`}
                />
              }
            >
              {t('ai.investigate.listItem.createBtn')}
            </ArrButton>
          </Box>
        )}
        {flatItems.length > 0 &&
          sortedGroups.map(([dateLabel, group]: any, index) => (
            <React.Fragment key={dateLabel}>
              <ListSubheader
                disableGutters
                className="typo-text label3_size date-header"
                data-testid={`my-transcript-date-header-${(dateLabel ?? '')
                  .trim()
                  .replaceAll(/\s+/g, '-')
                  .replaceAll(/[^a-zA-Z0-9-]/g, '')}`}
              >
                <Typography
                  variant="caption"
                  data-testid={`my-transcripts-date-${index}`}
                >
                  {dateLabel}
                </Typography>
              </ListSubheader>

              {group.items.map((item: any) => {
                const currentIndex = flatIndexCounter;
                flatIndexCounter += 1;

                return (
                  <TranscriptListItem
                    key={item.transcriptId}
                    item={item}
                    index={currentIndex}
                    focusedId={focusedId}
                    setFocusedId={setFocusedId}
                    orderedIds={orderedIds}
                    focusViaKeyboardRef={focusViaKeyboardRef}
                    selected={selectedTranscriptId === item.transcriptId}
                    showAvatar={false}
                    onClick={(clickedItem) =>
                      onTranscriptSelect?.({
                        transcriptId: clickedItem.transcriptId,
                        isOwner: clickedItem.isMyTranscript,
                        indexId: clickedItem.indexID,
                        isActiveIndex: clickedItem.isActiveIndex,
                        indexName: clickedItem.indexName,
                        transcriptName: clickedItem.transcriptName,
                        ownerName: clickedItem.ownerName,
                        documentCount: clickedItem.indexDocumentCount,
                      })
                    }
                    onDelete={handleDeleteTranscript}
                    onRename={handleRenameTranscript}
                  />
                );
              })}
            </React.Fragment>
          ))}

        {isFetching && flatItems.length > 0 && (
          <Box className="spinner-bottom">
            <ArrLoadingSpinner size={24} />
          </Box>
        )}
      </List>
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          status={[ToastType.Error]}
          onClose={() => setShowToast(false)}
          title="Error"
          data-testid="edit-transcript-toast"
          setErrorIconAsAlert
        />
      )}
    </Box>
  );
};

export default MyTranscripts;
